// Global JavaScript for Digital Lab Empire Theme

// Cart functionality
class CartManager {
  constructor() {
    this.notificationElement = null;
    this.notificationTimer = null;
    this.notificationRemoveTimer = null;
    this.init();
  }

  init() {
    this.updateCartCount();
    this.setupCartListeners();
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      const cartCountElements = document.querySelectorAll('#cart-count, .cart-count');
      cartCountElements.forEach(el => {
        if (el) el.textContent = cart.item_count;
      });
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  setupCartListeners() {
    // Listen for cart updates
    document.addEventListener('cart:updated', () => {
      this.updateCartCount();
    });
  }

  async addToCart(variantId, quantity = 1) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity
        })
      });

      if (response.ok) {
        const item = await response.json();
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { item: item }
        }));
        return item;
      } else {
        const error = await response.json();
        throw new Error(error.description || 'Error adding to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showNotification(error.message || 'Error adding to cart', 'error');
      throw error;
    }
  }

  showNotification(message, type = 'success') {
    clearTimeout(this.notificationTimer);
    clearTimeout(this.notificationRemoveTimer);
    if (this.notificationElement) {
      this.notificationElement.remove();
    }

    const notification = document.createElement('div');
    notification.className = `fixed top-4 right-4 z-50 px-6 py-4 rounded-lg shadow-lg transform transition-all duration-300 ${
      type === 'success' ? 'bg-green-500' : 'bg-red-500'
    } text-white`;
    notification.textContent = message;
    notification.setAttribute('role', type === 'error' ? 'alert' : 'status');
    notification.setAttribute('aria-live', type === 'error' ? 'assertive' : 'polite');
    this.notificationElement = notification;

    document.body.appendChild(notification);

    this.notificationTimer = setTimeout(() => {
      notification.classList.add('translate-x-full', 'opacity-0');
      this.notificationRemoveTimer = setTimeout(() => {
        notification.remove();
        if (this.notificationElement === notification) {
          this.notificationElement = null;
        }
      }, 300);
    }, 3000);
  }
}

// Initialize cart manager
const cartManager = new CartManager();

// Search functionality
const searchInput = document.querySelector('input[name="q"]');
if (searchInput) {
  let searchTimeout;
  searchInput.addEventListener('input', function() {
    clearTimeout(searchTimeout);
    const query = this.value;
    
    if (query.length > 2) {
      searchTimeout = setTimeout(() => {
        performSearch(query);
      }, 300);
    }
  });
}

async function performSearch(query) {
  try {
    const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}`);
    const data = await response.json();
    displaySearchResults(data);
  } catch (error) {
    console.error('Search error:', error);
  }
}

function displaySearchResults(data) {
  // Implement search results display
  console.log('Search results:', data);
}

function toggleTestimonialCard(card) {
  const expanded = card.getAttribute('aria-expanded') === 'true';
  card.classList.toggle('expanded', !expanded);
  card.setAttribute('aria-expanded', String(!expanded));
}

function scrollCatalogCarousel(control) {
  const targetId = control.dataset.carouselTarget || '';
  const directionValue = control.dataset.carouselDirection;
  const direction = directionValue === '-1' ? -1 : directionValue === '1' ? 1 : 0;
  const carousel = targetId ? document.getElementById(targetId) : null;
  const carouselFrame = control.closest('.catalog-carousel');

  if (!carousel || !carouselFrame || !carouselFrame.contains(carousel) || !direction) return;

  const scrollAmount = Math.min(Math.max(carousel.clientWidth * 0.82, 260), 420);
  carousel.scrollBy({
    left: direction * scrollAmount,
    behavior: 'smooth'
  });
}

document.addEventListener('click', event => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const carouselControl = target.closest('[data-carousel-control]');
  if (carouselControl) {
    scrollCatalogCarousel(carouselControl);
    return;
  }

  const testimonialCard = target.closest('[data-testimonial-card]');
  if (testimonialCard) toggleTestimonialCard(testimonialCard);
});

document.addEventListener('keydown', event => {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  const target = event.target instanceof Element ? event.target : null;
  const testimonialCard = target ? target.closest('[data-testimonial-card]') : null;
  if (!testimonialCard || target !== testimonialCard) return;

  event.preventDefault();
  toggleTestimonialCard(testimonialCard);
});

// Lazy loading images
if ('IntersectionObserver' in window) {
  const imageObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const img = entry.target;
        if (img.dataset.src) {
          img.src = img.dataset.src;
          img.removeAttribute('data-src');
          observer.unobserve(img);
        }
      }
    });
  });

  document.querySelectorAll('img[data-src]').forEach(img => {
    imageObserver.observe(img);
  });
}

// Form validation
function validateForm(form) {
  const inputs = form.querySelectorAll('input[required], textarea[required]');
  let isValid = true;

  inputs.forEach(input => {
    if (!input.value.trim()) {
      isValid = false;
      input.classList.add('border-red-500');
    } else {
      input.classList.remove('border-red-500');
    }
  });

  return isValid;
}

// Add to cart function for product cards
async function addToCartFromCard(button) {
  const variantId = button.getAttribute('data-variant-id');
  
  if (!variantId) {
    console.error('Variant ID not found');
    alert('Error: Product variant not found');
    return;
  }

  // Disable button to prevent multiple clicks
  button.disabled = true;
  const originalText = button.textContent;
  button.textContent = 'Adding...';

  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: parseInt(variantId),
        quantity: 1
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.description || 'Failed to add product to cart');
    }

    const data = await response.json();
    
    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: { item: data }
    }));
    
  } catch (error) {
    console.error('Error adding to cart:', error);
    alert(error.message || 'Error adding product to cart. Please try again.');
  } finally {
    // Re-enable button
    button.disabled = false;
    button.textContent = originalText;
  }
}

// Export for use in other scripts
window.CartManager = CartManager;
window.cartManager = cartManager;
window.addToCartFromCard = addToCartFromCard;








